import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '剧情介绍', to: '/story' },
  { label: '演员详情', to: '/cast' },
  { label: '影视资讯', to: '/news' },
  { label: '关于', to: '/about' },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const solid = scrolled || !isHome || menuOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-gradient-to-b from-background-50/80 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          <Link to="/" className="group flex items-center gap-3 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-primary-500/50 text-primary-500">
              <i className="ri-film-line text-lg"></i>
            </span>
            <span className="min-w-0">
              <span className="block truncate font-heading text-sm md:text-base font-bold tracking-wide text-foreground-950">
                我的朋友他的妻子
              </span>
              <span className="block truncate text-[10px] tracking-[0.28em] text-foreground-500 font-label">
                FILM ARCHIVE
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `relative whitespace-nowrap rounded-md px-4 py-2 text-sm transition-colors ${
                    isActive
                      ? 'text-primary-400'
                      : 'text-foreground-300 hover:text-foreground-950'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={submitSearch} className="hidden md:flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100/70 px-3 py-1.5 focus-within:border-primary-500/60">
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索剧情 / 演员 / 资讯"
                aria-label="站内搜索"
                className="w-36 lg:w-48 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
              />
            </form>

            <button
              type="button"
              aria-label="打开搜索"
              onClick={() => navigate('/search')}
              className="md:hidden flex h-10 w-10 items-center justify-center rounded-md border border-background-300/70 text-foreground-300 cursor-pointer"
            >
              <i className="ri-search-line text-base"></i>
            </button>

            <button
              type="button"
              aria-label="展开导航菜单"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="lg:hidden flex h-10 w-10 items-center justify-center rounded-md border border-background-300/70 text-foreground-300 cursor-pointer"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200/70 bg-background-50/95 backdrop-blur-md">
          <div className="w-full px-4 md:px-8 py-3">
            <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索剧情 / 演员 / 资讯"
                aria-label="站内搜索"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
              />
            </form>
            <div className="flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                      isActive ? 'text-primary-400 bg-background-100' : 'text-foreground-300'
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-base"></i>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}